
interface Step1BasicInfoProps {
  formData: {
    name: string;
    slug: string;
    description: string;
  };
  errors: Record<string, string>;
  onChange: (field: string, value: string) => void;
}

export function Step1BasicInfo({ formData, errors, onChange }: Step1BasicInfoProps) {
  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    onChange('name', value);

    // Auto-generate slug if slug is empty or matches previous auto-generated pattern
    if (!formData.slug || formData.slug === slugify(formData.name)) {
      onChange('slug', slugify(value));
    }
  };

  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Create New Platform</h2>
        <p className="mt-2 text-sm text-gray-600">
          Let's start with the basics. Enter your platform's name and choose a unique identifier.
        </p>
      </div>

      <div>
        <label
          htmlFor="platform-name"
          className="block text-sm font-semibold text-gray-900 mb-2"
        >
          Platform Name <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          id="platform-name"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g., Acme Payments"
          className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.name
              ? 'border-red-600 bg-red-50'
              : 'border-gray-300 focus:border-blue-500'
          }`}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="platform-slug"
          className="block text-sm font-semibold text-gray-900 mb-2"
        >
          Platform Slug <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          id="platform-slug"
          value={formData.slug}
          onChange={(e) => onChange('slug', e.target.value.toLowerCase())}
          placeholder="acme-payments"
          className={`w-full px-3 py-2.5 border rounded-md font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.slug
              ? 'border-red-600 bg-red-50'
              : 'border-gray-300 focus:border-blue-500'
          }`}
        />
        {formData.slug && !errors.slug && (
          <p className="mt-1 text-sm text-gray-600">
            Your platform will be available at:{' '}
            <span className="font-mono font-medium">
              {formData.slug}.appedge.com
            </span>
          </p>
        )}
        {errors.slug && (
          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {errors.slug}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="platform-description"
          className="block text-sm font-semibold text-gray-900 mb-2"
        >
          Description <span className="text-sm font-normal text-gray-500">(Optional)</span>
        </label>
        <textarea
          id="platform-description"
          value={formData.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="A brief description of your payment platform..."
          rows={4}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="mt-1 text-xs text-gray-500">
          This will be visible to your platform users.
        </p>
      </div>
    </div>
  );
}
